import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { useAuthStore } from '../stores/auth-store'
import { logout, refreshAccessToken } from './auth-service'
import router from '../router'

/**
 * Axios instance that will include the token in all requests
 */
class ApiClient {
  private axiosInstance: AxiosInstance
  private isRefreshing = false
  private failedQueue: Array<{
    resolve: (value?: any) => void
    reject: (reason?: any) => void
  }> = []

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private processQueue(error: any, token: string | null = null) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error)
      } else {
        prom.resolve(token)
      }
    })

    this.failedQueue = []
  }

  private setupInterceptors() {
    // Request interceptor to add the auth token
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const authStore = useAuthStore()
        if (authStore.accessToken) {
          config.headers['Authorization'] = `Bearer ${authStore.accessToken}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )

    // Response interceptor to handle auth errors
    this.axiosInstance.interceptors.response.use(
      (response) => {
        return response
      },
      async (error) => {
        const originalRequest = error.config

        // Handle 401 Unauthorized errors
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // If a refresh is already in progress, queue this request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject })
            })
              .then((token) => {
                originalRequest.headers['Authorization'] = `Bearer ${token}`
                return this.axiosInstance(originalRequest)
              })
              .catch((err) => {
                return Promise.reject(err)
              })
          }

          originalRequest._retry = true
          this.isRefreshing = true

          try {
            // Try to refresh the access token
            const newAccessToken = await refreshAccessToken()

            if (newAccessToken) {
              // Update the original request with the new token
              originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`
              // Process all queued requests with the new token
              this.processQueue(null, newAccessToken)
              // Retry the original request
              return this.axiosInstance(originalRequest)
            } else {
              // If refresh token failed, logout the user
              console.error('Failed to refresh token. Logging out.')
              this.processQueue(new Error('Failed to refresh token'), null)
              await logout(router, true) // Force logout with router
              return Promise.reject(error)
            }
          } catch (refreshError) {
            // If refresh token request returns bad request or any error, logout
            console.error('Error refreshing token. Logging out.')
            this.processQueue(refreshError, null)
            await logout(router, true)
            return Promise.reject(refreshError)
          } finally {
            this.isRefreshing = false
          }
        }
        return Promise.reject(error)
      },
    )
  }

  // HTTP methods
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.get<T>(url, config)
    return response.data
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data, config)
    return response.data
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.put<T>(url, data, config)
    return response.data
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.delete<T>(url, config)
    return response.data
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.patch<T>(url, data, config)
    return response.data
  }

  async getBlob(url: string, config?: AxiosRequestConfig): Promise<{ data: Blob; headers: Record<string, any> }> {
    const response = await this.axiosInstance.get(url, {
      ...config,
      responseType: 'blob',
    })
    return { data: response.data, headers: response.headers }
  }
}

// Create and export a singleton instance
const apiClient = new ApiClient()

// Export the singleton instance for use in components
export const useApiClient = () => {
  return apiClient
}

export default apiClient
